<template>
  <project-one-toolbar-tab-layout id="project-one-toolbar-customer-info" :title="bp('toolbarCustomerInfo')">
    <form @submit.prevent="submitCustomerData">
      <form-group
        v-model="customerName"
        v-validate="'required'"
        :data-vv-as="bp('customerName')"
        :label="bp('customerName')"
        :error="bp('required')"
        :maxlength="200"
        type="text"
        name="customer-name"
        qaid="p1-customer-info-name"
        required
        :disabled="isP1ConfigInCart"
        :show-error-messages="fieldHasError('customer-name') ? true : false"
        @input="fieldHasError('customer-name')"
      />
      <form-group
        v-model="customerEmail"
        v-validate="'required|email'"
        :data-vv-as="bp('customerEmail')"
        :label="bp('customerEmail')"
        :error="bp('required')"
        :maxlength="200"
        type="email"
        name="email"
        qaid="p1-customer-info-email"
        required
        :disabled="isP1ConfigInCart"
        :show-error-messages="fieldHasError('email') ? true : false"
        @input="fieldHasError('email')"
      />
      <!-- Phone number not required -->
      <form-group
        v-model="customerPhone"
        :label="bp('customerPhone')"
        :maxlength="26"
        type="tel"
        name="phone"
        :disabled="isP1ConfigInCart"
        qaid="p1-customer-info-phone"
      />
      <form-group
        v-model="customerCountry"
        v-validate="'required'"
        :data-vv-as="bp('selectCountry')"
        :label="bp('selectCountry')"
        :error="bp('required')"
        type="select"
        name="country"
        :multi-items="countriesList"
        qaid="p1-customer-info-country"
        required
        :disabled="isP1ConfigInCart"
        :show-error-messages="fieldHasError('country') ? true : false"
        @input="fieldHasError('country')"
        @change="setCustomerCountry"
      />
      <form-group
        v-model="customerAddress"
        v-validate="'required'"
        :data-vv-as="bp('customerAddress')"
        :label="bp('customerAddress')"
        :error="bp('required')"
        :maxlength="200"
        type="text"
        name="address"
        qaid="p1-customer-info-address"
        required
        :disabled="isP1ConfigInCart"
        :show-error-messages="fieldHasError('address') ? true : false"
        @input="fieldHasError('address')"
      />
      <form-group
        v-model="customerCity"
        v-validate="'required'"
        :data-vv-as="bp('customerCity')"
        :label="bp('customerCity')"
        :error="bp('required')"
        :maxlength="200"
        type="text"
        name="city"
        qaid="p1-customer-info-city"
        required
        :disabled="isP1ConfigInCart"
        :show-error-messages="fieldHasError('city') ? true : false"
        @input="fieldHasError('city')"
      />
      <!-- State input field swaps between required dropdown/select (if selected country code is US) or non-required input -->
      <form-group
        v-if="statesList.length > 0"
        v-model="customerState"
        v-validate="'required'"
        :data-vv-as="bp('customerState')"
        :label="bp('customerState')"
        :error="bp('required')"
        type="select"
        name="state"
        :multi-items="statesList"
        qaid="p1-customer-info-state"
        required
        :disabled="isP1ConfigInCart"
        :show-error-messages="fieldHasError('state') ? true : false"
        @input="fieldHasError('state')"
      />
      <!-- State not required if country is not US -->
      <form-group
        v-else
        v-model="customerState"
        :label="bp('customerState')"
        :maxlength="255"
        type="text"
        name="state"
        :disabled="isP1ConfigInCart"
        qaid="p1-customer-info-state"
      />
      <form-group
        v-model="customerZip"
        v-validate="'required'"
        :data-vv-as="bp('customerZip')"
        :label="bp('customerZip')"
        :error="bp('required')"
        :maxlength="20"
        type="text"
        name="zip"
        qaid="p1-customer-info-zip"
        required
        :disabled="isP1ConfigInCart"
        :show-error-messages="fieldHasError('zip') ? true : false"
        @input="fieldHasError('zip')"
      />
      <trek-button
        primary
        :disabled="isLoading || !isEditable || isP1ConfigInCart"
        type="button"
        qaid="p1-customer-info-save-btn"
        @click="submitCustomerData"
      >
        <span v-html="bp('save')" />
      </trek-button>
    </form>
  </project-one-toolbar-tab-layout>
</template>

<script>
import {mapState, mapMutations, mapActions} from 'vuex';
import ProjectOneToolbarTabLayout from '@/projectOne/components/toolbar/components/ProjectOneToolbarTabLayout';
import FormGroup from '@/components/FormGroup';
import TrekButton from '@/components/TrekButton';
import axios from 'axios';
import editableStatus from '@/projectOne/mixins/editableStatus';

export default {
  name: 'ProjectOneToolbarCustomerInfo',
  components: {
    ProjectOneToolbarTabLayout,
    FormGroup,
    TrekButton,
  },
  mixins: [editableStatus],
  data() {
    return {
      isLoading: false,
      countriesList: [],
      statesList: [],
    };
  },
  computed: {
    ...mapState('projectOne', ['customerInformation', 'bikeData', 'isP1ConfigInCart']),

    customerName: {
      get() {
        return this.customerInformation?.name ?? '';
      },
      set(name) {
        this.customerInformation.name = name;
      },
    },

    customerEmail: {
      get() {
        return this.customerInformation?.email ?? '';
      },
      set(email) {
        this.customerInformation.email = email;
      },
    },

    customerPhone: {
      get() {
        return this.customerInformation?.phone ?? '';
      },
      set(phone) {
        this.customerInformation.phone = phone;
      },
    },

    customerCountry: {
      get() {
        return this.customerInformation?.country ?? '';
      },
      set(country) {
        this.customerInformation.country = country;
      },
    },

    customerAddress: {
      get() {
        return this.customerInformation?.address ?? '';
      },
      set(address) {
        this.customerInformation.address = address;
      },
    },

    customerCity: {
      get() {
        return this.customerInformation?.city ?? '';
      },
      set(city) {
        this.customerInformation.city = city;
      },
    },

    customerState: {
      get() {
        return this.customerInformation?.state ?? '';
      },
      set(state) {
        this.customerInformation.state = state;
      },
    },

    customerZip: {
      get() {
        return this.customerInformation?.zip ?? '';
      },
      set(zip) {
        this.customerInformation.zip = zip;
      },
    },
  },
  mounted() {
    this.fetchCountriesList();
    this.fetchCustomerInfo().then(() => this.fetchStatesList());
  },
  methods: {
    ...mapMutations('projectOne', ['setCustomerState', 'setIsSaveModalVisible', 'setOnSaveCallbackFunction']),
    ...mapActions('projectOne', ['fetchCustomerInfo', 'saveCustomerInfo']),

    async fetchCountriesList() {
      try {
        const response = await axios.get(`address/countries/`);
        const countriesReturn = response.data.data;

        countriesReturn.forEach((country) => {
          this.countriesList.push({
            value: country.isocode,
            displayName: country.name,
          });
        });
      } catch (error) {
        console.error('countries error:', error);
        return error;
      }
    },

    async fetchStatesList() {
      this.statesList = [];

      if (this.customerCountry === 'US') {
        try {
          const response = await axios.get(`/address/regions/${this.customerCountry}`);
          const statesReturn = response.data.data;

          statesReturn.forEach((state) => {
            this.statesList.push({
              value: state.isocodeShort,
              displayName: state.name,
            });
          });
        } catch (error) {
          console.error('countries error:', error);
          return error;
        }
      }
    },

    setCustomerCountry(e) {
      this.customerCountry = e.target.value;
      this.setCustomerState(''); //Need this to empty state dropdown/reset statesList ONLY when country selection changes
      this.fetchStatesList();
    },

    fieldHasError(nameOfField) {
      return this.$validator.errors.items.find((o) => o.field == nameOfField);
    },

    submitCustomerData() {
      this.isLoading = true;

      if (this.bikeData?.code && this.bikeData?.description) {
        this.handleSubmission();
      } else {
        this.setOnSaveCallbackFunction(this.handleSubmission);
        this.setIsSaveModalVisible(true);
        this.isLoading = false;
      }
    },

    handleSubmission() {
      this.$validator
        .validateAll()
        .then((isValid) => {
          if (isValid) {
            let payload = {
              name: this.customerName,
              email: this.customerEmail,
              phone: this.customerPhone,
              country: this.customerCountry,
              address: this.customerAddress,
              city: this.customerCity,
              state: this.customerState,
              zip: this.customerZip,
            };

            this.saveCustomerInfo(payload);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
